import { useCallback } from 'react';

/**
 * Hook that returns a customRequest function for antd Upload components.
 * Handles authenticated uploads with token refresh.
 * Replaces aplanet-ui-kit's useCustomRequest.
 *
 * @param {object} options
 * @param {object} options.api_requests  Redux state with access_token
 * @param {function} options.refreshAccessToken  Action to refresh the access token
 * @returns {function} customRequest function compatible with antd Upload's customRequest prop
 */
const useCustomRequest = ({ api_requests, refreshAccessToken }) => {
  const customRequest = useCallback(async ({ action, file, onSuccess, onError, onProgress }) => {
    const doUpload = async (token) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(action, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      return response;
    };

    try {
      let response = await doUpload(api_requests.access_token);

      // If unauthorized, try refreshing the token
      if (response.status === 401 && refreshAccessToken) {
        refreshAccessToken();
        await new Promise(resolve => setTimeout(resolve, 1000));
        response = await doUpload(api_requests.access_token);
      }

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const text = await response.text();
      if (onSuccess) onSuccess(text);
    } catch (err) {
      if (onError) onError(err);
    }
  }, [api_requests, refreshAccessToken]);

  return customRequest;
};

export default useCustomRequest;
